import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import { deacService } from '../../../services/central-api/deac';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import { useAuth } from '../../../context/Auth';
import { getApplicationsStatus, useVacancyStatus } from '../Vacancies/VacanciesCommons';
import { DeacParamList } from '../../../typings/Params';
import Filter from './Filters';
import DeleteApplicationModal from './DeleteApplicationModal';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function Applications() {
    const route = useRoute<RouteProp<DeacParamList, 'Applications'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DeacParamList['Applications']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedVacancyApplications, setSelectedVacancyApplications] = useState<VacancyApplication[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);
    const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
    const [cancelApplicationModal, setCancelApplicationModal] = useState<boolean>(false);
    const [currentAction, setCurrentAction] = useState<'none' | 'cancelApplication'>('none');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacanciesApplications', filters],
        queryFn: () => deacService.getVacanciesApplications(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            setClearSelectedRows(false);
        }
    }, [error, isError]);

    useEffect(() => {
        setClearSelectedRows(false);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getActions() {
        const actions: Action[] = [];

        actions.push({
            content: translate('cancelApplication'),
            onPress: () => {
                setClearSelectedRows(false);
                setCurrentAction('cancelApplication');
            }
        });

        return actions;
    }

    return (
        <Container>
            <Header title={translate('candidaciesManagement')} />
            <Card>
                <DataTableHeader
                    title={translate('agentsCandidacies')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                        buttonType={currentAction === 'none' ? 'secondary' : 'tertiary'}
                    />}
                    actions={currentAction === 'none'
                        ? <DropdownButton
                            isButton
                            viewStyle={{ height: '100%' }}
                            actions={getActions()}
                            buttonIcon={faChevronDown}
                            offsetX={29}
                            offsetY={45}
                            size={172}
                        />
                        : <View style={{ flexDirection: 'row', gap: 10 }}>
                            <CustomButton
                                text={translate('cancelSelection')}
                                type='secondary'
                                onPress={() => {
                                    setClearSelectedRows(true);
                                    setSelectedVacancyApplications([]);
                                    setAllRowsSelected(false);
                                    setCurrentAction('none');
                                }}
                            />

                            <CustomButton
                                text={translate('concludeSelection')}
                                type='primary'
                                disabled={selectedVacancyApplications.length === 0 && !allRowsSelected}
                                onPress={() => {
                                    setCancelApplicationModal(true);
                                }}
                            />
                        </View>}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        clearSelectedRows={clearSelectedRows}
                        selectableRows={currentAction === 'cancelApplication'}
                        selectableRowDisabled={(row) => {
                            const vacancyStatus = useVacancyStatus(row.vacancy);
                            return (row.deletedAt !== null ||
                                vacancyStatus.isCanceled ||
                                vacancyStatus.isFinished ||
                                vacancyStatus.isProgress ||
                                vacancyStatus.isConfirmed
                            );
                        }}
                        onSelectedRowsChange={(selectedRows) => {
                            setSelectedVacancyApplications(selectedRows.selectedRows);
                            setAllRowsSelected(selectedRows.allSelected);
                        }}
                        columns={[
                            {
                                name: translate('status'),
                                cell: row => getApplicationsStatus(row),
                                grow: 0,
                            },
                            {
                                name: translate('agentName'),
                                cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                                grow: 1,
                            },
                            {
                                name: 'RF',
                                cell: row => row.user.registry,
                                grow: 1,
                            },
                            {
                                name: translate('role'),
                                cell: row => row.user.role.name,
                                grow: 1,
                            },
                            {
                                name: translate('agentActingBody'),
                                cell: row => row.user.actingBodyUnit.actingBody.name,
                                grow: 1,
                                omit: !authenticatedUser?.permissions.deac_manage_all_acting_bodies_applications
                            },
                            {
                                name: translate('agentUnit'),
                                cell: row => row.user.actingBodyUnit.name,
                                grow: 1,
                                omit: !(authenticatedUser?.permissions.deac_manage_all_acting_bodies_applications ||
                                    authenticatedUser?.permissions.deac_manage_acting_body_units_applications)
                            },
                            {
                                name: translate('activityTitle'),
                                cell: row => row.vacancy.title,
                                grow: 1,
                            },
                            {
                                name: translate('appliedIn'),
                                cell: row => moment(row.createdAt).format('DD/MM/YYYY'),
                                grow: 1,
                            },
                            {
                                name: translate('actingBody'),
                                cell: row => row.vacancy.workplace.actingBodyUnit.actingBody.name,
                                grow: 1,
                            },
                            {
                                name: translate('command'),
                                cell: row => row.vacancy.workplace.actingBodyCommand.name,
                                grow: 1,
                            },
                            {
                                name: translate('unit'),
                                cell: row => row.vacancy.workplace.actingBodyUnit.name,
                                grow: 1,
                            },
                            {
                                name: translate('canceledIn'),
                                cell: row => row.deletedAt && moment(row.deletedAt).format('DD/MM/YYYY'),
                                grow: 1,
                            },
                            {
                                name: translate('canceledBy'),
                                cell: row => row.deletedBy?.warName && row.deletedBy.warName != '' ? row.deletedBy.warName : row.deletedBy?.name,
                                grow: 1,
                            },
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        }}
                    />}
                />
            </Card>
            <DeleteApplicationModal
                isModalVisible={cancelApplicationModal}
                setModalVisible={setCancelApplicationModal}
                onConfirm={() => {
                    setClearSelectedRows(true);
                    setCancelApplicationModal(false);
                    setSelectedVacancyApplications([]);
                    setAllRowsSelected(false);
                    setCurrentAction('none');
                    refetch();
                }}
                applicationsIds={selectedVacancyApplications.map(a => a.id)}
            />
        </Container>
    );
}
